import { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { enrollmentReportService } from '@/api/domains/attendance/services/enrollmentReport.service';
import { formatDateToYYYYMMDD } from '@/utils';
import type { EnrollmentReport } from '@/api/domains/attendance/types/enrollmentReport.types';
import type { DateRangeValue } from '@/components/ui/filters/DateRangePicker';
import type { TableColumn } from '@/components/ui/tables/Table.types';


const DEFAULT_PAGE_SIZE = 8;
// Opciones del selector de filas; debe incluir DEFAULT_PAGE_SIZE.
export const ENROLLMENT_PAGE_SIZE_OPTIONS = [8, 10, 25, 50, 100] as const;
// Rango inicial: un mes atrás hasta hoy, sobre la fecha de registro del enrolamiento.
const DEFAULT_RANGE_MONTHS = 1;

// Equivalencia entre la clave de columna y el campo de orden del backend.
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  fullName: 'fullName',
  deviceName: 'deviceName',
  hasPin: 'hasPin',
  hasFingerprint: 'hasFingerprint',
  hasPalm: 'hasPalm',
  hasFace: 'hasFace',
  hasPhoto: 'hasPhoto',
  zktecoCount: 'zktecoFingerprintCount',
  uareUCount: 'uareUFingerprintCount',
  hu20Count: 'hu20FingerprintCount',
};

interface FilterState {
  dateFrom: string;
  dateTo: string;
}

export interface UseEnrollmentReportResult {
  rows: EnrollmentReport[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<EnrollmentReport>[];
  onDateRangeChange: (range: DateRangeValue) => void;
  dateFrom: string;
  dateTo: string;
  isLoading: boolean;
}

export function useEnrollmentReport(): UseEnrollmentReportResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { canView, isLoaded } = usePagePermissions();

  const initialFilters = useMemo<FilterState>(() => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setMonth(fromDate.getMonth() - DEFAULT_RANGE_MONTHS);
    return {
      dateFrom: formatDateToYYYYMMDD(fromDate),
      dateTo: formatDateToYYYYMMDD(today),
    };
  }, []);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<EnrollmentReport> | null> => {
      const response = await enrollmentReportService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:enrollmentReport.messages.loadError'));
        return null;
      }

      return {
        items: response.data.items,
        totalRecords: response.data.pagination.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    filters,
    setFilters,
  } = usePaginatedList<EnrollmentReport, FilterState>({
    fetchFn,
    initialFilters,
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    enabled: isLoaded && canView,
  });

  // El rango vive en `filters`: al cambiarlo, usePaginatedList refetchea y resetea la página.
  const onDateRangeChange = useCallback((range: DateRangeValue) => {
    if (!range.from || !range.to) return;
    setFilters({ dateFrom: range.from, dateTo: range.to });
  }, [setFilters]);

  const renderFlag = useCallback(
    (value: boolean) => (
      value
        ? translate('attendance:enrollmentReport.values.yes')
        : translate('attendance:enrollmentReport.values.no')
    ),
    [translate],
  );

  const columns: TableColumn<EnrollmentReport>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('attendance:enrollmentReport.columns.rut'),
    },
    {
      key: 'fullName',
      header: translate('attendance:enrollmentReport.columns.employee'),
    },
    {
      key: 'deviceName',
      header: translate('attendance:enrollmentReport.columns.device'),
    },
    {
      key: 'hasPin',
      header: translate('attendance:enrollmentReport.columns.pin'),
      render: (row: EnrollmentReport) => renderFlag(row.hasPin),
    },
    {
      key: 'hasFingerprint',
      header: translate('attendance:enrollmentReport.columns.fingerprint'),
      render: (row: EnrollmentReport) => renderFlag(row.hasFingerprint),
    },
    {
      key: 'hasPalm',
      header: translate('attendance:enrollmentReport.columns.palm'),
      render: (row: EnrollmentReport) => renderFlag(row.hasPalm),
    },
    {
      key: 'hasFace',
      header: translate('attendance:enrollmentReport.columns.face'),
      render: (row: EnrollmentReport) => renderFlag(row.hasFace),
    },
    {
      key: 'hasPhoto',
      header: translate('attendance:enrollmentReport.columns.photo'),
      render: (row: EnrollmentReport) => renderFlag(row.hasPhoto),
    },
    {
      key: 'zktecoCount',
      header: translate('attendance:enrollmentReport.columns.zkteco'),
    },
    {
      key: 'uareUCount',
      header: translate('attendance:enrollmentReport.columns.uareU'),
    },
    {
      key: 'hu20Count',
      header: translate('attendance:enrollmentReport.columns.hu20'),
    },
  ], [translate, renderFlag]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onDateRangeChange,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    isLoading: loading,
  };
}

export default useEnrollmentReport;
